import { useMemo } from 'react';
import { getCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  LessonsForTeachingBlockVersionDocument,
  type LessonsForTeachingBlockVersionQuery,
  type LessonsForTeachingBlockVersionQueryVariables,
  TeachingBlockResourceType,
  TeachingBlockVersionDataDocument,
  type TeachingBlockVersionDataQuery,
  type TeachingBlockVersionDataQueryVariables,
  useTeachingBlockVersionDataQuery,
} from '../../../types/planung-graphql-client-defs';
import { urqlClient } from '../../../utils/urqlClient';

export type TeachingBlockVersionDataType = NonNullable<TeachingBlockVersionDataQuery['teachingBlockVersionData']>;
export type TeachingBlockVersionDataCard = TeachingBlockVersionDataType['cards'][0];

/**
 * Die Antwort enthält nur die Typen des Lesemodells. Mutationen an Version, Karten, Ausschlüssen
 * und Zuordnungen liefern dagegen die Knotentypen bzw. `LessonItem`; damit sie das Lesemodell
 * weiter verwerfen, hängen deren Typnamen hier an.
 *
 * Jeder Aufrufer der Abfrage muss diesen Context mitgeben (auch der Routen-Loader): der Cache merkt
 * sich die Typnamen an der Antwort, die zuerst vom Server kommt.
 */
export const VERSION_DATA_QUERY_CONTEXT = {
  additionalTypenames: [
    'TeachingBlockVersion',
    'TeachingBlockCard',
    'TeachingBlockVersionCard',
    'TeachingBlockAvailabilityExclusion',
    'TeachingBlockVersionExclusion',
    'TEACHINGBLOCK_LESSONS',
    'Lesson',
    'LessonItem',
  ],
};

const uuidsOfType = (data: TeachingBlockVersionDataType | undefined, type: TeachingBlockResourceType): string[] =>
  data?.resources.filter((resource) => resource.resourceType === type).map((resource) => resource.uuid) ?? [];

/**
 * Lesemodell einer Epochenplan-Version: Kopfdaten, Ressourcen, Karten und Ausschlüsse in einer
 * Anfrage. Alle Verweise sind uuids; Stammdaten und Zuordnungen kommen aus ihren eigenen Abfragen.
 */
export const useTeachingBlockVersionData = (versionUuid: string | undefined) => {
  const [{ data, fetching }] = useTeachingBlockVersionDataQuery({
    variables: { versionUuid: versionUuid ?? '' },
    pause: !versionUuid,
    context: VERSION_DATA_QUERY_CONTEXT,
  });

  const versionData = data?.teachingBlockVersionData ?? undefined;

  const resourceUuids = useMemo(
    () => ({
      classes: uuidsOfType(versionData, TeachingBlockResourceType.Class),
      teachers: uuidsOfType(versionData, TeachingBlockResourceType.Teacher),
      rooms: uuidsOfType(versionData, TeachingBlockResourceType.Room),
      subjects: uuidsOfType(versionData, TeachingBlockResourceType.Subject),
    }),
    [versionData],
  );

  return { versionData, resourceUuids, fetching };
};

/**
 * Lädt Lesemodell und Zuordnungen der Version frisch vom Server. Nach jeder Folge mehrerer
 * Mutationen aufrufen (z. B. „in dieser Version bearbeiten“: anlegen, Karten löschen, lösen).
 *
 * Grund: der Dokument-Cache von urql merkt sich erst beim Eintreffen einer Antwort, welche Typen an
 * einer Abfrage hängen. Verwirft die erste Mutation die Abfrage, läuft das Neuladen noch, während
 * die nächsten Mutationen schon fertig sind; die verwerfen dann nichts mehr, und die (vor ihnen
 * berechnete) Antwort bleibt als veralteter Stand stehen.
 */
export const refreshTeachingBlockVersionQueries = async (versionUuid: string): Promise<void> => {
  if (!versionUuid) return;
  await Promise.all([
    urqlClient
      .query<TeachingBlockVersionDataQuery, TeachingBlockVersionDataQueryVariables>(
        TeachingBlockVersionDataDocument,
        { versionUuid },
        { ...VERSION_DATA_QUERY_CONTEXT, requestPolicy: 'network-only' },
      )
      .toPromise(),
    urqlClient
      .query<LessonsForTeachingBlockVersionQuery, LessonsForTeachingBlockVersionQueryVariables>(
        LessonsForTeachingBlockVersionDocument,
        { versionUuid },
        { ...getCacheTag('LESSONS'), requestPolicy: 'network-only' },
      )
      .toPromise(),
  ]);
};
